import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import ActionMenu, { type ActionMenuItem } from "../ActionMenu";

interface ChangeStepMenuProps {
  /** The step failed: Edit and Swap say they recover it. */
  failed: boolean;
  busy: boolean;
  /** Skip, as module C offers it to this viewer (W-22 slice 2): `null` when not offered; `unavailable`, why not. */
  skip: { readonly unavailable?: string } | null;
  /** Edit, swap and a new step: the host's. */
  hostChanges: boolean;
  /** Earlier steps that ran, by key, to run again from. */
  earlier: readonly string[];
  stepName: (stepKey: string) => string;
  onSkip: () => void;
  onEdit: () => void;
  onSwap: () => void;
  onNewStep: () => void;
  onRunAgainFrom: (stepKey: string) => void;
}

/**
 * Everything that changes the current step instead of running it (F56), under one button:
 * skip it, edit it, swap it for another, add a new one, or run again from an earlier step.
 * Each edit opens a Thread (F51, ADR-0031 D2); none opens the Workflow editor.
 */
export default function ChangeStepMenu({ failed, busy, skip, hostChanges, earlier, stepName, onSkip, onEdit, onSwap, onNewStep, onRunAgainFrom }: ChangeStepMenuProps) {
  const { t } = useTranslation();
  const items: ActionMenuItem[] = [
    ...(skip === null ? [] : [{ id: "skip", label: t("threads.current.skip"), onSelect: onSkip, disabled: busy, unavailable: skip.unavailable }]),
    ...(hostChanges
      ? [
          { id: "edit", label: t(failed ? "threads.current.editAndTryAgain" : "threads.current.edit"), onSelect: onEdit },
          { id: "swap", label: t(failed ? "threads.current.recoveryStep" : "threads.current.swap"), onSelect: onSwap },
          { id: "newStep", label: t("threads.current.newStep"), onSelect: onNewStep },
        ]
      : []),
    ...runAgainItems(earlier, stepName, busy, onRunAgainFrom, t),
  ];
  return <ActionMenu label={t("threads.current.change")} items={items} />;
}

interface RunAgainMenuProps {
  earlier: readonly string[];
  stepName: (stepKey: string) => string;
  busy: boolean;
  onRunAgainFrom: (stepKey: string) => void;
}

/** Earlier steps to run again from, on their own once no step is current, as when the Workflow is done. */
export function RunAgainMenu({ earlier, stepName, busy, onRunAgainFrom }: RunAgainMenuProps) {
  const { t } = useTranslation();
  return <ActionMenu label={t("threads.current.runAgain")} items={runAgainItems(earlier, stepName, busy, onRunAgainFrom, t)} />;
}

/** "Run again from ‹step›" for each earlier step. */
function runAgainItems(earlier: readonly string[], stepName: (stepKey: string) => string, busy: boolean, onRunAgainFrom: (stepKey: string) => void, t: TFunction): ActionMenuItem[] {
  return earlier.map((stepKey) => ({ id: `again:${stepKey}`, label: t("threads.current.runAgainFrom", { step: stepName(stepKey) }), onSelect: () => { onRunAgainFrom(stepKey); }, disabled: busy }));
}
